import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  getScaleFromIncrement,
  NumericField,
  toBigWithDefault,
  type CareOrder,
  type Currency,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import type { WritableDraft } from 'immer';
import {
  fillAmountDiffersFromDerived,
  fillAmountExceedsIncrementPrecision,
  fillPriceExceedsIncrementPrecision,
  fillQtyEqualsCareOrderLeavesQty,
  fillQtyExceedsIncrementPrecision,
} from './fieldValidation';

export type ManualFillCareOrderLegSummary = NonNullable<CareOrder['LegSummary']>[number];

interface ManualFillCareOrderEntryFields {
  rowID: string;
  fillPrice: NumericField;
  fillQty: NumericField;
  fillAmount: NumericField;
  amountIncrement?: string;
  priceIncrement?: string;
  quantityIncrement?: string;
  // True means the field follows its derived value. A trader edit flips the
  // field out of auto mode; Reset writes the derived value and opts back in.
  isAutoPopulating: {
    fillPrice: boolean;
    fillAmount: boolean;
  };
}

export interface ManualFillCareOrderLegEntry extends ManualFillCareOrderEntryFields {
  legSummary: ManualFillCareOrderLegSummary;
}

export interface ManualFillCareOrderEntry extends ManualFillCareOrderEntryFields {
  careOrder: CareOrder;
  legEntries?: ManualFillCareOrderLegEntry[];
}

export interface ManualFillCareOrdersState {
  entries: ManualFillCareOrderEntry[];
}

interface ManualFillCareOrderEntryActionPayload {
  careOrderID: string;
  legSymbol?: string;
}

type ManualFillCareOrderUpdateTarget = ManualFillCareOrderEntry | ManualFillCareOrderLegEntry;
// Minimal shape needed by the derivation helpers. Parent entries and leg entries
// both satisfy this without forcing the helpers to depend on their full types.
type ManualFillDerivationTarget = {
  fillPrice: { value?: string };
  fillQty: { value?: string };
  fillAmount: { value?: string };
  amountIncrement?: string;
  priceIncrement?: string;
  legEntries?: { fillAmount: { value?: string } }[];
};

export const EMPTY_MANUAL_FILL_CARE_ORDERS_STATE: ManualFillCareOrdersState = {
  entries: [],
};

const manualFillCareOrdersSlice = createSlice({
  name: 'manualFillCareOrdersSlice',
  initialState: EMPTY_MANUAL_FILL_CARE_ORDERS_STATE,
  reducers: {
    setEntryFillPrice(
      state,
      action: PayloadAction<ManualFillCareOrderEntryActionPayload & { value: string | undefined }>
    ) {
      updateEntryField(state.entries, action.payload, (entry, parentEntry) => {
        entry.fillPrice = updateFillPriceField(entry, {
          value: action.payload.value,
          isTouched: true,
          isProgrammatic: false,
        });

        refreshAutoPopulatedFillAmount(entry);
        refreshParentAutoPopulatedFields(parentEntry);
      });
    },
    setEntryFillQty(
      state,
      action: PayloadAction<ManualFillCareOrderEntryActionPayload & { value: string | undefined }>
    ) {
      updateEntryField(state.entries, action.payload, (entry, parentEntry) => {
        entry.fillQty = updateFillQtyField(entry, { value: action.payload.value, isTouched: true });

        refreshAutoPopulatedFields(entry);
        refreshParentAutoPopulatedFields(parentEntry);
      });
    },
    setEntryFillAmount(
      state,
      action: PayloadAction<ManualFillCareOrderEntryActionPayload & { value: string | undefined }>
    ) {
      updateEntryField(state.entries, action.payload, (entry, parentEntry) => {
        entry.fillAmount = updateFillAmountField(entry, {
          value: action.payload.value,
          isTouched: true,
          isProgrammatic: false,
        });

        refreshAutoPopulatedFillPrice(entry);
        refreshParentAutoPopulatedFields(parentEntry);
      });
    },
    resetEntryFillAmountToDerived(state, action: PayloadAction<ManualFillCareOrderEntryActionPayload>) {
      updateEntryField(state.entries, action.payload, (entry, parentEntry) => {
        entry.fillAmount = updateFillAmountField(entry, { value: getDerivedManualFillAmount(entry)?.toFixed() });

        refreshAutoPopulatedFillPrice(entry);
        refreshParentAutoPopulatedFields(parentEntry);
      });
    },
    resetEntryFillPriceToDerived(state, action: PayloadAction<ManualFillCareOrderEntryActionPayload>) {
      updateEntryField(state.entries, action.payload, entry => {
        entry.fillPrice = updateFillPriceField(entry, { value: getDerivedManualFillPrice(entry)?.toFixed() });
      });
    },
    removeEntries(state, action: PayloadAction<{ careOrderIDs: string[] }>) {
      const careOrderIDs = new Set(action.payload.careOrderIDs);
      state.entries = state.entries.filter(entry => !careOrderIDs.has(entry.careOrder.OrderID));
    },
    touchAll(state) {
      state.entries.forEach(entry => {
        touchEntryFields(entry);
        entry.legEntries?.forEach(legEntry => touchEntryFields(legEntry));
      });
    },
  },
});

function manualFillCareOrdersHaveInvalidValues(state: ManualFillCareOrdersState): boolean {
  return state.entries.some(entry => {
    return entryHasInvalidValue(entry) || entry.legEntries?.some(legEntry => entryHasInvalidValue(legEntry)) === true;
  });
}

/** "Master" validation function, used to block submission if anything is wrong */
export function canManualFillCareOrders(
  state: ManualFillCareOrdersState
): { valid: true } | { valid: false; reason: string } {
  if (state.entries.length === 0) {
    return { valid: false, reason: 'No care orders available to manually fill' };
  }

  if (manualFillCareOrdersHaveInvalidValues(state)) {
    return { valid: false, reason: 'Invalid manual fill values provided' };
  }

  return { valid: true };
}

export const manualFillCareOrdersSliceActions = { ...manualFillCareOrdersSlice.actions };
export const manualFillCareOrdersSliceReducer = manualFillCareOrdersSlice.reducer;

/**
 * Creates dialog-local manual-fill state from the selected care orders and the
 * increment metadata used for rounding and advisory precision warnings.
 */
export function careOrdersToManualFillState(
  careOrders: CareOrder[],
  refData: {
    currenciesBySymbol?: Map<string, Currency>;
    securitiesBySymbol?: Map<string, Security>;
  } = {}
): ManualFillCareOrdersState {
  const currenciesBySymbol = refData.currenciesBySymbol ?? new Map();
  const securitiesBySymbol = refData.securitiesBySymbol ?? new Map();
  const entries = careOrders.map(careOrder =>
    createManualFillEntry(careOrder, {
      currenciesBySymbol,
      securitiesBySymbol,
    })
  );

  return {
    entries,
  };
}

/** Helper for updating some entry in the entries array corresponding to the provided care order id */
function updateEntryField(
  entries: WritableDraft<ManualFillCareOrderEntry>[],
  { careOrderID, legSymbol }: ManualFillCareOrderEntryActionPayload,
  updateEntry: (
    entry: WritableDraft<ManualFillCareOrderUpdateTarget>,
    parentEntry?: WritableDraft<ManualFillCareOrderEntry>
  ) => void
) {
  const entry = entries.find(entry => entry.careOrder.OrderID === careOrderID);
  if (!entry) {
    return;
  }

  if (!legSymbol) {
    updateEntry(entry);
    return;
  }

  const legEntry = entry.legEntries?.find(legEntry => legEntry.legSummary.Symbol === legSymbol);
  if (!legEntry) {
    return;
  }

  updateEntry(legEntry, entry);
}

interface ManualFillFieldUpdate {
  value?: string | undefined;
  isTouched?: boolean;
  isProgrammatic?: boolean;
}

/** Updates the price field, and runs validation */
function updateFillPriceField(
  entry: WritableDraft<ManualFillCareOrderUpdateTarget>,
  update: ManualFillFieldUpdate = {}
) {
  let field = entry.fillPrice;
  if ('value' in update) {
    const isProgrammatic = update.isProgrammatic !== false;
    field = field.updateValue(update.value, isProgrammatic);
    entry.isAutoPopulating.fillPrice = isProgrammatic;
  }
  if (update.isTouched !== undefined) {
    field = field.setTouched(update.isTouched);
  }

  return field.validate([fillPriceExceedsIncrementPrecision], {
    priceIncrement: entry.priceIncrement,
  });
}

/** Updates the qty field, and runs validation */
function updateFillQtyField(entry: WritableDraft<ManualFillCareOrderUpdateTarget>, update: ManualFillFieldUpdate = {}) {
  let field = entry.fillQty;
  if ('value' in update) {
    field = field.updateValue(update.value);
  }
  if (update.isTouched !== undefined) {
    field = field.setTouched(update.isTouched);
  }

  return field.validate([fillQtyEqualsCareOrderLeavesQty, fillQtyExceedsIncrementPrecision], {
    leavesQty: getManualFillEntryLeavesQty(entry),
    quantityIncrement: entry.quantityIncrement,
  });
}

/**
 * Updates the amount field, and runs validation. If the update is programmatic, it will not set isTouched = true.
 */
function updateFillAmountField(
  entry: WritableDraft<ManualFillCareOrderUpdateTarget>,
  update: ManualFillFieldUpdate = {}
) {
  let field = entry.fillAmount;
  if ('value' in update) {
    const isProgrammatic = update.isProgrammatic !== false;
    field = field.updateValue(update.value, isProgrammatic);
    entry.isAutoPopulating.fillAmount = isProgrammatic;
  }
  if (update.isTouched !== undefined) {
    field = field.setTouched(update.isTouched);
  }

  return field.validate([fillAmountExceedsIncrementPrecision, fillAmountDiffersFromDerived], {
    amountIncrement: entry.amountIncrement,
    derivedFillAmount: getDerivedManualFillAmount(entry),
  });
}

function shouldAutoDeriveFillAmountField(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  return entry.isAutoPopulating.fillAmount;
}

function shouldAutoDeriveFillPriceField(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  return entry.isAutoPopulating.fillPrice;
}

function refreshAutoPopulatedFields(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  refreshAutoPopulatedFillAmount(entry);
  refreshAutoPopulatedFillPrice(entry);
}

function refreshAutoPopulatedFillAmount(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  if (shouldAutoDeriveFillAmountField(entry)) {
    entry.fillAmount = updateFillAmountField(entry, { value: getDerivedManualFillAmount(entry)?.toFixed() });
    return;
  }

  entry.fillAmount = updateFillAmountField(entry);
}

function refreshAutoPopulatedFillPrice(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  if (!shouldAutoDeriveFillPriceField(entry)) {
    return;
  }

  entry.fillPrice = updateFillPriceField(entry, { value: getDerivedManualFillPrice(entry)?.toFixed() });
}

function refreshParentAutoPopulatedFields(entry: WritableDraft<ManualFillCareOrderEntry> | undefined) {
  if (!entry) {
    return;
  }

  refreshAutoPopulatedFields(entry);
}

/**
 * Derives the cash Fill Amount from Fill Qty and Fill Price, rounded to the
 * amount increment scale using half-up rounding.
 *
 * Compound parent rows are the exception: their Fill Amount is the exact sum
 * of leg Fill Amounts. We preserve the leg-derived decimals and let validation
 * warn if the parent amount exceeds the preferred currency increment.
 */
export function getDerivedManualFillAmount(entry: ManualFillDerivationTarget) {
  if (entry.legEntries != null && entry.legEntries.length > 0) {
    if (entry.legEntries.some(legEntry => !legEntry.fillAmount.value)) {
      return undefined;
    }

    return entry.legEntries.reduce((sum, legEntry) => sum.plus(toBigWithDefault(legEntry.fillAmount.value, 0)), Big(0));
  }

  if (!entry.fillQty.value || !entry.fillPrice.value) {
    return undefined;
  }

  const unroundedFillAmount = toBigWithDefault(entry.fillQty.value, 0).times(
    toBigWithDefault(entry.fillPrice.value, 0)
  );
  return unroundedFillAmount.round(getScaleFromIncrement(entry.amountIncrement), Big.roundHalfUp);
}

/**
 * Derives Fill Price from Fill Amount and Fill Qty, rounded to the price
 * increment scale using half-up rounding. Only compound parent rows opt into
 * this by default.
 */
export function getDerivedManualFillPrice(entry: ManualFillDerivationTarget) {
  if (!entry.fillQty.value || !entry.fillAmount.value) {
    return undefined;
  }

  const fillQty = toBigWithDefault(entry.fillQty.value, 0);
  if (fillQty.eq(0)) {
    return undefined;
  }

  const unroundedFillPrice = toBigWithDefault(entry.fillAmount.value, 0).div(fillQty);
  return unroundedFillPrice.round(getScaleFromIncrement(entry.priceIncrement), Big.roundHalfUp);
}

function createManualFillNumericField(name: string, value?: string, disabled = false) {
  return new NumericField({
    name,
    value,
    isRequired: !disabled,
    minimum: '0',
    isDisabled: disabled,
  }).validate();
}

// Given a care order and some ref data, builds the entry and attaches leg entries for any available LegSummary rows.
// Also runs initial field validation so we don't end up in an initial state without validation having run, making submission enabled
function createManualFillEntry(
  careOrder: CareOrder,
  refData: {
    currenciesBySymbol?: Map<string, Currency>;
    securitiesBySymbol?: Map<string, Security>;
  } = {}
): ManualFillCareOrderEntry {
  const inKindCareOrder = careOrder.isInKind === true;

  // Map every LegSummary entry to one leg entry in our manual fills slice to appear under the care order row
  const legEntries =
    careOrder.LegSummary != null && careOrder.LegSummary.length > 0
      ? careOrder.LegSummary?.map(legSummary => {
          const legEntry: ManualFillCareOrderLegEntry = {
            rowID: `${careOrder.OrderID}:${legSummary.Symbol}`,
            legSummary,
            fillPrice: createManualFillNumericField('Fill Price', undefined, inKindCareOrder),
            // The backend only accepts manual fills for the full remaining quantity, so leg qty defaults to that value.
            fillQty: createManualFillNumericField('Fill Quantity', legSummary.LeavesQty),
            fillAmount: createManualFillNumericField('Fill Amount', undefined, inKindCareOrder),
            amountIncrement: refData.currenciesBySymbol?.get(legSummary.AmountCurrency)?.MinIncrement,
            priceIncrement: refData.securitiesBySymbol?.get(legSummary.Symbol)?.MinPriceIncrement,
            quantityIncrement: refData.securitiesBySymbol?.get(legSummary.Symbol)?.MinSizeIncrement,
            // For legs, fillPrice is not (never) auto-populated, but fillAmount is
            isAutoPopulating: {
              fillPrice: false,
              fillAmount: true,
            },
          };

          return {
            ...legEntry,
            // We update each field (no-op) to run validation on init. It needs the entire leg entry, so we have to do it here
            fillPrice: updateFillPriceField(legEntry),
            fillQty: updateFillQtyField(legEntry),
            fillAmount: updateFillAmountField(legEntry),
          };
        })
      : undefined;

  // Create the top-level entry, where we finally potentially attach the leg entries
  const entry: ManualFillCareOrderEntry = {
    rowID: careOrder.OrderID,
    careOrder,
    fillPrice: createManualFillNumericField('Fill Price', undefined, inKindCareOrder),
    // The backend only accepts manual fills for the full remaining quantity, so parent qty defaults to that value.
    fillQty: createManualFillNumericField('Fill Quantity', careOrder.LeavesQty),
    fillAmount: createManualFillNumericField('Fill Amount', undefined, inKindCareOrder),
    amountIncrement: refData.currenciesBySymbol?.get(careOrder.AmountCurrency)?.MinIncrement,
    priceIncrement: refData.securitiesBySymbol?.get(careOrder.Symbol)?.MinPriceIncrement,
    quantityIncrement: refData.securitiesBySymbol?.get(careOrder.Symbol)?.MinSizeIncrement,
    // For the parent level, fillPrice is auto-populated if we have any legEntries
    isAutoPopulating: {
      fillPrice: legEntries != null && legEntries.length > 0,
      fillAmount: true,
    },
    legEntries,
  };

  return {
    ...entry,
    // We update each field (no-op) to run validation on init. It needs the entire entry, so we have to do it here
    fillPrice: updateFillPriceField(entry),
    fillQty: updateFillQtyField(entry),
    fillAmount: updateFillAmountField(entry),
  };
}

function touchEntryFields(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  entry.fillPrice = updateFillPriceField(entry, { isTouched: true });
  entry.fillQty = updateFillQtyField(entry, { isTouched: true });
  entry.fillAmount = updateFillAmountField(entry, { isTouched: true });
}

function entryHasInvalidValue(entry: ManualFillCareOrderUpdateTarget) {
  return entry.fillPrice.hasInvalidValue || entry.fillQty.hasInvalidValue || entry.fillAmount.hasInvalidValue;
}

function getManualFillEntryLeavesQty(entry: WritableDraft<ManualFillCareOrderUpdateTarget>) {
  return 'legSummary' in entry ? entry.legSummary.LeavesQty : entry.careOrder.LeavesQty;
}
